import React, { memo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Stack,
  LoadingButton,
  Icon,
  Divider,
  Tooltip,
  IconButton,
  Typography,
  CircularProgress
} from 'investira.react.components';
import { dates } from 'investira.sdk';
import { PageTemplate } from '../../../components/templates';
import services from '../../../services';
import AvaliacoesOutputHighlights from './AvaliacoesOutputHighlights';
import AvaliacoesOutputResumo from './AvaliacoesOutputResumo';
import AvaliacoesOutputPerfomance from './AvaliacoesOutputPerfomance';
import AvaliacaoOutputHeader from './AvaliacaoOutputHeader';
import AvaliacoesOutputRespostas from './AvaliacoesOutputRespostas/AvaliacoesOutputRespostas';

const pvCalcularProximaAvaliacao = pModified => {
  if (!pModified) {
    return null;
  }

  const xData = new Date(pModified);

  if (Number.isNaN(xData.getTime())) {
    return null;
  }

  return dates.addMonths(xData, 6);
};

/**
 * Obtém o ativo cujo ISIN corresponde ao usado na pesquisa.
 *
 * @param {Object} pResponse Resposta do serviço de pesquisa de ativos.
 * @param {string} pIsin ISIN informado na pesquisa.
 * @returns {Object|null}
 */
export const obterAtivoPesquisa = (pResponse, pIsin) => {
  const xData = pResponse?.data?.data || pResponse?.data || [];
  const xAtivos = Array.isArray(xData) ? xData : Object.values(xData || {});
  const xIsin = String(pIsin || '').toUpperCase();

  return (
    xAtivos.find(pAtivo => String(pAtivo?.isin || '').toUpperCase() === xIsin) || xAtivos[0] || null
  );
};

// Presets temporários enquanto o endpoint não retorna as notas e performances processadas.
const wConjuntosMock = {
  BROIT1CTF005: {
    notas: { notaQualitativa: 2.8, notaQuantitativa: 2.6 },
    performance: {
      notasGestor: [2.7, 2.5, 2.8],
      notasEntidade: [2.9, 2.7, 2.85]
    },
    resumo: {
      desempenhoQuantitativo: 'forte',
      periodoMeses: 36,
      rankingPosicao: 4,
      rankingTotal: 120,
      capacidadeFiduciaria: 'forte',
      garantiaConflitos: 'plena',
      atendimentoRegulamento: 'plenamente'
    }
  },
  BRLAW1CTF001: {
    notas: { notaQualitativa: 2.15, notaQuantitativa: 1.9 },
    performance: {
      notasGestor: [2, 1.8, 2.1],
      notasEntidade: [2.2, 2, 2.05]
    },
    resumo: {
      desempenhoQuantitativo: 'modesto',
      periodoMeses: 24,
      rankingPosicao: 32,
      rankingTotal: 120,
      capacidadeFiduciaria: 'modesta',
      garantiaConflitos: 'razoável',
      atendimentoRegulamento: 'razoavelmente'
    }
  },
  BRSPX2CTF004: {
    notas: { notaQualitativa: 1.35, notaQuantitativa: 1.6 },
    performance: {
      notasGestor: [1.3, 1.5, 1.4],
      notasEntidade: [1.4, 1.2, 1.55]
    },
    resumo: {
      desempenhoQuantitativo: 'fraco',
      periodoMeses: 12,
      rankingPosicao: 84,
      rankingTotal: 120,
      capacidadeFiduciaria: 'fraca',
      garantiaConflitos: 'baixa',
      atendimentoRegulamento: 'insatisfatoriamente'
    }
  },
  generico: {
    notas: { notaQualitativa: 2.5, notaQuantitativa: 2 },
    performance: {
      notasGestor: [2.4, 2.1, 2.3],
      notasEntidade: [2.6, 2.3, 2.5]
    },
    resumo: {
      desempenhoQuantitativo: 'modesto',
      periodoMeses: 36,
      rankingPosicao: 18,
      rankingTotal: 100,
      capacidadeFiduciaria: 'modesta',
      garantiaConflitos: 'razoável',
      atendimentoRegulamento: 'razoavelmente'
    }
  }
};

export const obterConjuntoMock = pIsin => {
  return wConjuntosMock[String(pIsin)] || wConjuntosMock.generico;
};

export const calcularNotasAvaliacao = (
  pAvaliacao,
  pNotasFallback = wConjuntosMock.generico.notas
) => {
  const xNotaQualitativa = Number(pAvaliacao?.nota_qualitativa ?? pNotasFallback.notaQualitativa);
  const xNotaQuantitativa = Number(
    pAvaliacao?.nota_quantitativa ?? pNotasFallback.notaQuantitativa
  );
  const xNotaGeralContrato = pAvaliacao?.nota_geral;
  const xNotaGeral =
    xNotaGeralContrato !== null && xNotaGeralContrato !== undefined
      ? Number(xNotaGeralContrato)
      : xNotaQualitativa * 0.7 + xNotaQuantitativa * 0.3;

  return {
    notaGeral: Number.isFinite(xNotaGeral) ? xNotaGeral : 0,
    notaQualitativa: Number.isFinite(xNotaQualitativa) ? xNotaQualitativa : 0,
    notaQuantitativa: Number.isFinite(xNotaQuantitativa) ? xNotaQuantitativa : 0
  };
};

const AvaliacoesOutput = memo(() => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [rData, setRData] = useState(null);
  const [gestorDetalhes, setGestorDetalhes] = useState(null);
  const [ativoDetalhes, setAtivoDetalhes] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let xAtivo = true;

    setLoading(true);
    setFetchError(null);
    setRData(null);
    setGestorDetalhes(null);
    setAtivoDetalhes(null);

    if (id) {
      services.avaliacoes.concluir({ avaliacao_id: id }, rRes => {
        console.log('Status: Concluído');
      });

      services.respostas.list(
        { avaliacao_id: id },
        rRes => {
          if (!xAtivo) {
            return;
          }

          const xAvaliacao = rRes?.data || null;
          setRData(xAvaliacao);
          const xPessoaId = xAvaliacao?.gestor_pessoa_id;
          const xIsin = xAvaliacao?.ativo?.isin;
          let xRequestsPendentes = Number(Boolean(xPessoaId)) + Number(Boolean(xIsin));

          const pvFinalizarRequestComplementar = () => {
            xRequestsPendentes -= 1;

            if (xAtivo && xRequestsPendentes === 0) {
              setLoading(false);
            }
          };

          if (xRequestsPendentes === 0) {
            setLoading(false);
            return;
          }

          if (xPessoaId) {
            services.pessoas.list(
              { pessoa_id: xPessoaId },
              rPessoaRes => {
                if (!xAtivo) {
                  return;
                }

                const xPessoa = Array.isArray(rPessoaRes?.data) ? rPessoaRes.data[0] : null;
                setGestorDetalhes(xPessoa || null);
              },
              () => {
                if (xAtivo) {
                  setGestorDetalhes(null);
                }
              },
              pvFinalizarRequestComplementar
            );
          }

          if (xIsin) {
            services.pesquisas.ativo(
              { pesquisa: xIsin },
              rAtivoRes => {
                if (xAtivo) {
                  setAtivoDetalhes(obterAtivoPesquisa(rAtivoRes, xIsin));
                }
              },
              () => {
                if (xAtivo) {
                  setAtivoDetalhes(null);
                }
              },
              pvFinalizarRequestComplementar
            );
          }
        },
        () => {
          if (!xAtivo) {
            return;
          }

          setFetchError('Erro ao carregar resultado da avaliação');
          setLoading(false);
        }
      );
    } else {
      setLoading(false);
    }

    return () => {
      xAtivo = false;
    };
  }, [id]);

  const xRespostas = Array.isArray(rData?.respostas) ? rData.respostas : [];
  const xQuantidadeJustificativas = xRespostas.filter(pResposta => {
    return String(pResposta?.revisor_justificativa || '').trim().length > 0;
  }).length;
  const xGestor = {
    ...(rData?.gestor || {}),
    ...(gestorDetalhes || {})
  };
  const xTitle =
    xGestor?.pessoa_fantasia || xGestor?.pessoa || rData?.gestor_email || 'Resultado da avaliação';
  const xProximaAvaliacao = pvCalcularProximaAvaliacao(rData?.modified);
  const xPessoaId = gestorDetalhes?.pessoa_id ?? rData?.gestor_pessoa_id;
  const xIsin = gestorDetalhes?.ativo?.isin ?? rData?.ativo?.isin;
  const xConjuntoMock = obterConjuntoMock(xIsin);
  const xNotas = calcularNotasAvaliacao(rData, xConjuntoMock.notas);

  if (loading) {
    return (
      <PageTemplate title={'Carregando...'} subtitle={'Avaliação Gestor e Fundo'} maxWidth={'xl'}>
        <Stack alignItems="center" justifyContent="center" py={8}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary" mt={2}>
            Carregando resultado da avaliação...
          </Typography>
        </Stack>
      </PageTemplate>
    );
  }
  if (fetchError) {
    return (
      <PageTemplate title={xTitle} subtitle={'Avaliação Gestor e Fundo'} maxWidth={'xl'}>
        <Stack alignItems="center" justifyContent="center" py={8}>
          <Typography variant="h6" color="error">
            {fetchError}
          </Typography>
        </Stack>
      </PageTemplate>
    );
  }
  if (!rData) {
    return (
      <PageTemplate title={xTitle} subtitle={'Avaliação Gestor e Fundo'} maxWidth={'xl'}>
        <Stack alignItems="center" justifyContent="center" py={8}>
          <Typography variant="h6" color="textSecondary">
            Nenhum resultado disponível para esta avaliação
          </Typography>
        </Stack>
      </PageTemplate>
    );
  }
  return (
    <PageTemplate
      title={xTitle}
      subtitle={'Avaliação Gestor e Fundo'}
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2} alignItems="center">
          {/* <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="download" size={16} />}
            size="small"
            sx={{
              px: 2
            }}>
            PDF
          </LoadingButton> */}
          <Tooltip title={`Docs`}>
            <IconButton color="primary" size="small" onClick={() => navigate(`/avaliacoes/docs`)}>
              <Icon iconName="question" size={24} />
            </IconButton>
          </Tooltip>
        </Stack>
      }>
      <Stack gap={3}>
        <Stack gap={1}>
          <AvaliacaoOutputHeader
            created={rData.created}
            gestor={xGestor}
            usuarioName={rData.usuario_name}
            usuarioUsername={rData.usuario_username}
          />
          <Divider />
        </Stack>
        <Stack gap={2} flexGrow={1} direction="row">
          <AvaliacoesOutputHighlights
            nota={xNotas.notaGeral}
            notaQualitativa={xNotas.notaQualitativa}
            notaQuantitativa={xNotas.notaQuantitativa}
            proximaAvaliacao={xProximaAvaliacao}
            justificativas={xQuantidadeJustificativas}
          />
          <AvaliacoesOutputPerfomance
            respostas={xRespostas}
            performanceMock={xConjuntoMock.performance}
          />
        </Stack>
        <Divider />
        <AvaliacoesOutputResumo
          ativo={ativoDetalhes}
          gestor={xGestor}
          nota={xNotas.notaGeral}
          notas={xNotas}
          resumo={rData.resumo}
          resumoMock={xConjuntoMock.resumo}
        />
        <Divider />
        <AvaliacoesOutputRespostas data={xRespostas} />
      </Stack>
    </PageTemplate>
  );
});
AvaliacoesOutput.displayName = 'AvaliacoesOutput';
export default AvaliacoesOutput;
